'use client'

import { Icon } from '@iconify/react'
import { motion, useReducedMotion } from 'framer-motion'
import { Avatar, AvatarFallback, AvatarImage } from '@mdxui/primitives'
import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import { CodeWindow } from '../code-window'

export interface FeaturesAgentFlowProps {
  title?: string
  description?: string
  /** AI agent avatar */
  agent?: {
    name: string
    avatar?: string
  }
  /** Integration icon (Iconify name or URL) */
  integrationIcon?: string
  /** Code to display in the code window */
  code?: string
  /** Programming language for syntax highlighting */
  language?: string
  className?: string
}

const defaultCode = `import { Octokit } from '@octokit/rest'

const octokit = new Octokit({
  auth: process.env.GITHUB_TOKEN
})

// AI Agent commits changes
await octokit.repos.createOrUpdateFileContents({
  owner: 'your-org',
  repo: 'your-repo',
  path: 'src/features/new-feature.ts',
  message: 'feat: implement new feature via AI agent',
  content: Buffer.from(code).toString('base64'),
  branch: 'main'
})`

/**
 * FeaturesAgentFlow - AI agent capability showcase
 *
 * Shows a single AI agent connecting to a provider (e.g., GitHub) via an
 * animated connection line, with a code window demonstrating it in action.
 *
 * Best for: AI/automation landing pages, developer tools (Services/Agents dialect)
 */
export function FeaturesAgentFlow({
  title = 'AI-Powered Development',
  description = 'Our AI agents seamlessly integrate with your development workflow. They can read, write, and commit code directly to your repositories, automating repetitive tasks and accelerating your development process.',
  agent = { name: 'AI', avatar: '/avatars/Sam.png' },
  integrationIcon = 'logos:github-icon',
  code = defaultCode,
  language = 'typescript',
  className,
}: FeaturesAgentFlowProps) {
  const isIconUrl = integrationIcon.startsWith('http') || integrationIcon.startsWith('/')
  const prefersReducedMotion = useReducedMotion()

  // Custom grid with fadeSize 0.3 (smaller visible area for this component)
  const fadeSize = 0.3
  const sizeX = `${fadeSize * 100}%`
  const sizeY = `${fadeSize * 100 * 0.85}%`
  const maskGradient = `
    repeating-linear-gradient(to right, black 0px, black 3px, transparent 3px, transparent 8px),
    repeating-linear-gradient(to bottom, black 0px, black 3px, transparent 3px, transparent 8px),
    radial-gradient(ellipse ${sizeX} ${sizeY} at 50% 0%, #000 60%, transparent 100%)
  `

  return (
    <section className={cn('relative py-32', className)}>
      <div
        className="absolute inset-0 z-0 dark:opacity-70"
        style={{
          backgroundImage: `
            linear-gradient(to right, var(--border) 1px, transparent 1px),
            linear-gradient(to bottom, var(--border) 1px, transparent 1px)
          `,
          backgroundSize: '20px 20px',
          backgroundPosition: '0 0, 0 0',
          maskImage: maskGradient,
          WebkitMaskImage: maskGradient,
          maskComposite: 'intersect',
          WebkitMaskComposite: 'source-in',
        }}
      />
      <div className="container mx-auto relative z-10 flex flex-col items-center gap-5">
        <div className="relative my-4 flex flex-row items-center">
          {/* AI Agent Avatar */}
          <div className="relative z-10 flex size-16 items-center justify-center rounded-[var(--radius-xl,calc(var(--radius)*1.5))] border border-border bg-gradient-to-b from-background to-muted shadow-[0_1px_0_0_rgba(255,255,255,0.1)_inset,0_4px_6px_-1px_rgba(0,0,0,0.1),0_8px_16px_-4px_rgba(0,0,0,0.1)] md:size-20">
            <Avatar className="size-10 md:size-14">
              <AvatarImage src={agent.avatar} alt={agent.name} />
              <AvatarFallback className="text-lg font-semibold">
                {agent.name.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
          </div>

          {/* Connection Line */}
          <div className="relative flex w-14 items-center justify-center md:w-20 lg:w-28">
            {/* Static line */}
            <div className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-current opacity-20" />
            {/* Animated syncing dots */}
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="absolute top-1/2 size-1 -translate-y-1/2 rounded-full bg-current"
                initial={{ left: '5%', opacity: 0 }}
                animate={
                  prefersReducedMotion
                    ? { left: '50%', opacity: 0.4 }
                    : {
                        left: ['5%', '95%'],
                        opacity: [0, 0.5, 0.5, 0],
                      }
                }
                transition={
                  prefersReducedMotion
                    ? { duration: 0 }
                    : {
                        duration: 2,
                        repeat: Infinity,
                        delay: i * 0.6,
                        ease: 'linear',
                      }
                }
              />
            ))}
          </div>

          {/* Integration Icon */}
          <div className="relative z-10">
            {/* Pulse ring */}
            {!prefersReducedMotion && (
              <motion.div
                className="absolute inset-0 rounded-[var(--radius-xl,calc(var(--radius)*1.5))] bg-primary/20"
                animate={{
                  scale: [1, 1.15, 1],
                  opacity: [0, 0.4, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeOut',
                }}
              />
            )}
            <div className="relative flex size-16 items-center justify-center rounded-[var(--radius-xl,calc(var(--radius)*1.5))] border border-border bg-gradient-to-b from-background to-muted shadow-[0_1px_0_0_rgba(255,255,255,0.1)_inset,0_4px_6px_-1px_rgba(0,0,0,0.1),0_8px_16px_-4px_rgba(0,0,0,0.1)] md:size-20">
              {isIconUrl ? (
                <img
                  src={integrationIcon}
                  alt="Integration"
                  className="size-8 dark:invert md:size-12"
                />
              ) : (
                <Icon
                  icon={integrationIcon}
                  className="size-8 dark:invert md:size-12"
                />
              )}
            </div>
          </div>
        </div>

        <div className="text-center">
          <h2 className="text-pretty text-3xl font-semibold lg:text-4xl">
            {title}
          </h2>
          <div className="mt-4 max-w-3xl">
            <Markdown size="base">{description}</Markdown>
          </div>
        </div>

        {/* Code Window */}
        <div className="relative mt-8 w-full max-w-4xl md:mt-10">
          <CodeWindow
            code={code}
            language={language}
            showHeader={false}
            className="rounded-[var(--radius-xl,calc(var(--radius)*1.5))]"
          />
        </div>
      </div>
    </section>
  )
}
